import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
} from "react-router-dom";
import { ChakraProvider, createSystem, defaultConfig } from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";

import {
  getBranches,
  getOrganizations,
  getServices,
  getServicesSubscriptions,
  getUsers,
} from "./store/actions/defaultAction";

import { updateAppStatus, verifyToken } from "./store/actions/authActions";
import { getIamRoles } from "./store/actions/permissionsActions";
import {
  listenToEvents,
  stopListeners,
} from "./store/actions/realtimeAction";

import { REPORT_RANGES } from "./store/constants";

// v2 RBAC
import { AbilityContext } from "./permissions/AbilityContext";
import { defineAbilityFor, defaultAbility } from "./permissions/ability";

import RoutesComponent from "./Routes";
import NoPermissions from "./pages/NoPermissions";
import Loader from "./components/loader";
import useClock from "./hooks/useClock";

import "./assets/css/All.css";
import "./assets/css/Print.css";
import "./assets/css/Inputs.css";

function App() {
  const dispatch = useDispatch();
  const timeNow = useClock();
  const {
    appStatus, userId, verifyStatus, userData, effectivePermissions,
  } = useSelector((state) => state.authReducer);
  const { organizationId, reportRange } = useSelector((state) => state.defaultReducer);
  const isLocalPreview = process.env.REACT_APP_QMR_PREVIEW_MODE === "true"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const previewUserData = isLocalPreview
    ? { id: "qmr-preview-user", organizationId: "qmr-preview-org", roles: [{ name: "SuperAdmin" }] }
    : userData;
  const previewPermissions = isLocalPreview
    ? [{ action: "manage", resource: "*", access: "any" }]
    : effectivePermissions;

  // Build CASL ability from effective permissions
  const ability = useMemo(() => {
    if (previewPermissions && previewPermissions.length > 0 && previewUserData?.organizationId) {
      return defineAbilityFor(previewPermissions, previewUserData.organizationId);
    }
    return defaultAbility;
  }, [previewPermissions, previewUserData?.organizationId]);

  useEffect(() => {
    if (!isLocalPreview) {
      dispatch(verifyToken());
    }
  }, [isLocalPreview]);
  useEffect(() => {
    if (!isLocalPreview && userId && verifyStatus === 1) {
      dispatch(getIamRoles());
    }
  }, [isLocalPreview, userId, verifyStatus]);
  useEffect(() => {
    if (!isLocalPreview && appStatus) {
      dispatch(getOrganizations());
    }
  }, [isLocalPreview, appStatus]);

  useEffect(() => {
    if (!isLocalPreview && organizationId.length > 0) {
      dispatch(getBranches(organizationId));
      dispatch(getServices(organizationId));
      dispatch(getServicesSubscriptions(organizationId));
      dispatch(getUsers(organizationId));
      dispatch(stopListeners());
      if (reportRange === REPORT_RANGES.RT) {
        dispatch(listenToEvents({ organizationId }));
      } else {
        dispatch(stopListeners());
      }
    }
  }, [isLocalPreview, organizationId, reportRange]);

  // v2 RBAC gate — unlock app when user has any QM read permission
  const hasAccess = ability.can('read', 'qm.reports') || ability.can('read', 'qm.dashboard');

  useEffect(() => {
    if (isLocalPreview || (hasAccess && verifyStatus === 1)) {
      dispatch(updateAppStatus(1));
    } else {
      dispatch(updateAppStatus(0));
    }
  }, [hasAccess, isLocalPreview, verifyStatus]);

  const system = createSystem(defaultConfig, {
    theme: {
      tokens: {
        fonts: {
          body: { value: "Lato, sans-serif" },
          heading: { value: "Lato, sans-serif" },
        },
      },
    },
  });

  const permissionsCheck = !hasAccess && !_.isEmpty(userData)
    ? <NoPermissions />
    : <Loader />;

  return (
    <ChakraProvider value={system}>
      <AbilityContext.Provider value={ability}>
        {
        !appStatus
          ? permissionsCheck
          : (
            <Router>
              <RoutesComponent />
            </Router>
          )
        }
      </AbilityContext.Provider>
    </ChakraProvider>
  );
}
export default App;
